import { useState, useEffect } from 'react';
import {
  Container,
  Tabs,
  Card,
  Stack,
  Text,
  Title,
  Group,
  Badge,
  Loader,
  Alert
} from '@mantine/core';
import {
  IconBuildingStore,
  IconBoxSeam,
  IconChartBar,
  IconExclamationCircle,
  IconSparkles
} from '@tabler/icons-react';
import { api } from '../../services/api';
import ProductManagement from './ProductManagement';
import SpecialRequestsList from '../special-requests/SpecialRequestsList';

const SellerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);

  useEffect(() => {
    fetchSellerProfile();
  }, []);

  const fetchSellerProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/princesses/profile/');
      setSellerProfile(response.seller_profile);
    } catch (err) {
      console.error('Error fetching seller profile:', err);
      setError(err.response?.data?.error || 'Failed to load princess profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert 
          icon={<IconExclamationCircle size="1rem" />} 
          color="red" 
          title="Error loading princess dashboard"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!sellerProfile) {
    return (
      <Container size="lg" py="xl">
        <Alert 
          icon={<IconExclamationCircle size="1rem" />} 
          color="blue" 
          title="No princess profile found"
        >
          You need to register as a princess first to access this dashboard.
        </Alert>
      </Container>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'green';
      case 'pending': return 'yellow';
      case 'rejected': return 'red';
      case 'suspended': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group align="apart" mb="xs">
            <Group align="center" gap="md">
              <IconBuildingStore size="2rem" />
              <div>
                <Title order={2}>{sellerProfile.business_name}</Title>
                <Text c="dimmed">{sellerProfile.business_type.replace('_', ' ')}</Text>
              </div>
            </Group>
            <Group gap="xs">
              <Badge color={getStatusColor(sellerProfile.seller_status)} variant="light">
                {sellerProfile.seller_status}
              </Badge>
              {sellerProfile.is_verified && (
                <Badge color="green" variant="light">
                  Verified
                </Badge>
              )}
            </Group>
          </Group>

          {sellerProfile.seller_status === 'pending' && (
            <Alert color="yellow" mt="md">
              Your princess account is pending approval. You'll be able to list products once approved.
            </Alert>
          )}

          {sellerProfile.seller_status === 'approved' && sellerProfile.is_verified && (
            <Alert color="green" mt="md">
              Your princess account is approved! You can now list and manage products.
            </Alert>
          )}

          {sellerProfile.seller_status === 'rejected' && (
            <Alert color="red" mt="md">
              Your princess application was rejected. Please contact support for more information.
            </Alert>
          )}

          {sellerProfile.seller_status === 'suspended' && (
            <Alert color="orange" mt="md">
              Your princess account is suspended. Please contact support for assistance.
            </Alert>
          )}
        </Card>

        {/* Dashboard Tabs */}
        <Tabs defaultValue="products" variant="outline">
          <Tabs.List>
            <Tabs.Tab value="products" leftSection={<IconBoxSeam size="0.8rem" />}>
              Products
            </Tabs.Tab>
            <Tabs.Tab value="special-requests" leftSection={<IconSparkles size="0.8rem" />}>
              Special Requests
            </Tabs.Tab>
            <Tabs.Tab value="analytics" leftSection={<IconChartBar size="0.8rem" />}>
              Analytics
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="products" pt="xs">
            <ProductManagement 
              sellerProfile={sellerProfile}
              canSell={sellerProfile.can_sell}
            />
          </Tabs.Panel>

          <Tabs.Panel value="special-requests" pt="xs">
            <SpecialRequestsList isPrincess={true} />
          </Tabs.Panel>

          <Tabs.Panel value="analytics" pt="xs">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={3} mb="md">Sales Analytics</Title>
              <Stack gap="md">
                <Group grow>
                  <Stack gap="xs" align="center">
                    <Text size="sm" c="dimmed">Total Sales</Text>
                    <Text size="xl" fw={700}>${sellerProfile.total_sales}</Text>
                  </Stack>
                  <Stack gap="xs" align="center">
                    <Text size="sm" c="dimmed">Total Orders</Text>
                    <Text size="xl" fw={700}>{sellerProfile.total_orders}</Text>
                  </Stack>
                  <Stack gap="xs" align="center">
                    <Text size="sm" c="dimmed">Average Rating</Text>
                    <Text size="xl" fw={700}>{sellerProfile.average_rating}/5</Text>
                  </Stack>
                  <Stack gap="xs" align="center">
                    <Text size="sm" c="dimmed">Total Reviews</Text>
                    <Text size="xl" fw={700}>{sellerProfile.total_reviews}</Text>
                  </Stack>
                </Group>
              </Stack>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

export default SellerDashboard;